import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Award, Search } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type {
  EventItem,
  EventPointsEntry,
  EventPointsEntryType,
} from '../../types/eventTeam';
import { PlayerProfile } from '../PlayerProfile';
import { PlayerSearchField, type PlayerSelection } from './PlayerSearchField';
import { cn } from '../../utils/cn';

export interface AwardPointsModalProps {
  isOpen: boolean;
  /** Any player's Minecraft UUID. The legacy prop name is kept for callers. */
  presetMemberUuid?: string;
  /** Pre-link an event. */
  presetEventId?: string;
  onClose: () => void;
  onSuccess: (entry: EventPointsEntry) => void;
}

/**
 * Award / deduct participation points for any player; requires `event_points`.
 * Delta is a signed non-zero integer; negative deltas default to `deduction`.
 * `prefix_redemption` entries are never posted manually (guide §7.5).
 */
export function AwardPointsModal({
  isOpen,
  presetMemberUuid,
  presetEventId,
  onClose,
  onSuccess,
}: AwardPointsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, () => { if (!submitting) onClose(); });

  const [player, setPlayer] = useState<PlayerSelection | null>(null);
  const memberUuid = player?.uuid ?? '';
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [entryType, setEntryType] = useState<EventPointsEntryType>('award');
  const [eventSearch, setEventSearch] = useState('');
  const debouncedEventSearch = useDebounce(eventSearch, 300);
  const [eventResults, setEventResults] = useState<EventItem[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setPlayer(presetMemberUuid ? { uuid: presetMemberUuid, name: null } : null);
    setDelta('');
    setReason('');
    setEntryType('award');
    setEventSearch('');
    setEventResults([]);
    setSelectedEvent(null);
    setError(null);
    if (presetEventId) {
      eventTeamApiService
        .getEvent(presetEventId)
        .then((e) => { if (!cancelled) setSelectedEvent(e); })
        .catch(() => { if (!cancelled) setError('The linked event could not be loaded. Close this form and try again.'); });
    }
    return () => { cancelled = true; };
  }, [isOpen, presetMemberUuid, presetEventId]);

  useEffect(() => {
    if (!isOpen || debouncedEventSearch.trim().length === 0) {
      setEventResults([]);
      return;
    }
    let cancelled = false;
    eventTeamApiService
      .searchEvents(debouncedEventSearch)
      .then((res) => {
        if (!cancelled) setEventResults(res);
      })
      .catch(() => {
        if (!cancelled) setEventResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedEventSearch, isOpen]);

  const deltaNum = Number(delta);
  const deltaValid = delta.trim() !== '' && Number.isSafeInteger(deltaNum) && deltaNum > 0;

  /**
   * The entry type dictates the sign (Roll's bug: "deduct 60" typed as `60` with
   * type=deduction was stored as +60 and INCREASED the total). Deduction always
   * sends a negative delta, award always positive; adjustment/auto keep the sign
   * exactly as typed.
   */
  const effectiveDelta = !deltaValid
    ? 0
    : entryType === 'deduction'
      ? -Math.abs(deltaNum)
      : entryType === 'award'
        ? Math.abs(deltaNum)
        : deltaNum;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !memberUuid || !deltaValid || !reason.trim() || (presetEventId && !selectedEvent)) return;
    setSubmitting(true);
    setError(null);
    try {
      const entry = await eventTeamApiService.awardPoints({
        member_uuid: memberUuid,
        delta: effectiveDelta,
        reason: reason.trim(),
        entry_type: entryType || undefined,
        event_id: selectedEvent?.id ?? null,
      });
      onSuccess(entry);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to award points');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-label="Player points"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-1 flex items-center gap-2">
          <Award className="w-6 h-6 text-yellow-400" />
          Award points
        </h2>
        <p className="text-sm text-glass-400 mb-4">Reward any player for taking part in events. Choose the amount and explain what they earned it for.</p>
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Player *</label>
            {presetMemberUuid ? (
              <PlayerProfile uuid={presetMemberUuid} showUuid={false} />
            ) : (
              <PlayerSearchField value={player} onChange={setPlayer} placeholder="Search any player by name or UUID…" />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block" htmlFor="reward-points">Points *</label>
              <input
                type="number"
                id="reward-points"
                min="1"
                step="1"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="e.g. 40"
                required
              />
              {delta.trim() !== '' && !deltaValid && (
                <p className="text-xs text-red-300 mt-0.5">Enter a positive whole number.</p>
              )}
              {deltaValid && (
                <p
                  className={cn(
                    'text-[10px] mt-0.5 font-medium',
                    effectiveDelta >= 0 ? 'text-green-300' : 'text-red-300',
                  )}
                >
                  Will apply: {effectiveDelta >= 0 ? `+${effectiveDelta}` : effectiveDelta} points
                </p>
              )}
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block" htmlFor="reward-action">Action</label>
              <select
                value={entryType}
                id="reward-action"
                onChange={(e) => setEntryType(e.target.value as EventPointsEntryType)}
                className="glass-input w-full text-sm capitalize"
              >
                <option value="award">Award points</option>
                <option value="deduction">Deduct points</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block" htmlFor="reward-reason">Reason *</label>
            <input
              type="text"
              id="reward-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="e.g. Halloween participation or tournament winner"
              required
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Linked event (optional)</label>
            {selectedEvent ? (
              <div className="flex items-center justify-between p-2 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                <span className="text-sm font-medium text-white truncate">{selectedEvent.title}</span>
                {!presetEventId && <button
                  type="button"
                  onClick={() => setSelectedEvent(null)}
                  className="text-glass-400 hover:text-white flex-shrink-0 p-1"
                >
                  <X className="w-4 h-4" />
                </button>}
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                <input
                  type="text"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  className="glass-input w-full pl-10 text-sm"
                  placeholder="Search events…"
                />
                {eventResults.length > 0 && eventSearch.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 glass-card border border-glass-200/20 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto">
                    {eventResults.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => {
                          setSelectedEvent(e);
                          setEventSearch('');
                          setEventResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-glass-200/10 text-sm text-white"
                      >
                        {e.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !memberUuid || !deltaValid || !reason.trim() || (!!presetEventId && !selectedEvent)}
              className="glass-button text-sm inline-flex items-center gap-1.5"
            >
              {submitting && <Loader className="w-4 h-4 animate-spin" />}
              {deltaValid && effectiveDelta < 0 ? `Deduct ${Math.abs(effectiveDelta)} points` : 'Award points'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
